import { useSyncExternalStore } from 'react';

/**
 * Story 24.7b — the keyboard technique (EXPERIENCE.md › Responsive &
 * Platform › Keyboard; DESIGN.md › Layout & Spacing). This module is its ONE
 * owner: the only `visualViewport` listener in the app. `window` and
 * `document` are touched only inside functions, never at import time.
 *
 * Two outputs:
 *
 * 1. `--kb-inset` on `<html>`: `max(0, innerHeight - vv.height -
 *    vv.offsetTop)` px. On iOS Safari the layout viewport does not shrink for
 *    the on-screen keyboard, so this is the keyboard's height and
 *    `h-viewport` (`src/index.css`) subtracts it. Under the
 *    `interactive-widget=resizes-content` viewport meta (Chromium) the layout
 *    viewport already shrank, `innerHeight` equals `vv.height`, and the
 *    inset is 0 — nothing is counted twice. Pinch-zoomed (scale > 1) the
 *    inset is 0 too, so zooming never resizes the app column.
 *
 * 2. The keyboard-open signal (`isKeyboardOpen`, `useKeyboardOpen`): an
 *    editable element has focus AND the visual viewport has shrunk by more
 *    than `KEYBOARD_SHRINK_THRESHOLD_PX` from its baseline. A focus without a
 *    shrink — a hardware keyboard on a tablet — is never "open". The shrink
 *    is measured on `vv.height * vv.scale`, so a pinch zoom (height halves,
 *    scale doubles) is no shrink. The baseline is the largest scaled height
 *    seen since `innerWidth` last changed: a rotation resets it.
 *
 * The tab bar reads the signal through `isTabBarHidden(location,
 * keyboardOpen)` — still the one hide path. Later stories (ThreadHeader,
 * composer) read `--kb-inset` and the signal; they never add a listener.
 *
 * Story 24.17 — the same listener also writes `--vv-height` (the scaled
 * visual-viewport height, px) and keeps the short-viewport signal
 * (`useShortViewport`): true while that height is under
 * `SHORT_VIEWPORT_PX`. Short screens cap the composer at 40% of
 * `--vv-height` and let the ThreadHeader scroll with the thread
 * (EXPERIENCE.md › Reflow; DESIGN.md › Layout & Spacing). Without
 * `visualViewport` it stays false and `--vv-height` is never written.
 */

export const KEYBOARD_SHRINK_THRESHOLD_PX = 150;

/** Story 24.17 — a visual viewport shorter than this is "short". */
export const SHORT_VIEWPORT_PX = 480;

const KB_INSET_PROPERTY = '--kb-inset';
const VV_HEIGHT_PROPERTY = '--vv-height';
const ZOOM_EPSILON = 0.01;

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'radio',
  'range',
  'color',
  'file',
  'submit',
  'reset',
  'image',
]);

let started = false;
let editableFocused = false;
let baseline = 0;
let lastInnerWidth: number | null = null;
let shrink = 0;
let open = false;
let short = false;
let teardown: (() => void) | null = null;
const subscribers = new Set<() => void>();

/** A textarea, a contenteditable element, or a text-like input. */
function isEditable(target: EventTarget | null): boolean {
  if (typeof Element === 'undefined' || !(target instanceof Element)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'textarea') return true;
  if (tag === 'input') {
    const type = (target.getAttribute('type') ?? 'text').toLowerCase();
    return !NON_TEXT_INPUT_TYPES.has(type);
  }
  if ((target as HTMLElement).isContentEditable) return true;
  // jsdom does not implement `isContentEditable`; the attribute is the
  // portable check. The NEAREST `[contenteditable]` decides, so a
  // `contenteditable="false"` island inside an editor is not editable.
  const host = target.closest('[contenteditable]');
  return host !== null && host.getAttribute('contenteditable') !== 'false';
}

function notify(): void {
  subscribers.forEach((listener) => listener());
}

function update(): void {
  const vv = window.visualViewport;
  if (!vv) return;

  const scaledHeight = vv.height * vv.scale;
  if (lastInnerWidth !== window.innerWidth) {
    // First reading, or a rotation: start the baseline afresh.
    lastInnerWidth = window.innerWidth;
    baseline = scaledHeight;
  } else {
    baseline = Math.max(baseline, scaledHeight);
  }
  shrink = baseline - scaledHeight;

  // Under a pinch zoom the visual viewport is shorter than the layout
  // viewport with no keyboard at all, so the inset is only measured at
  // (about) scale 1 — otherwise the app column would shrink while zoomed.
  const inset =
    vv.scale > ZOOM_EPSILON + 1 ? 0 : Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
  document.documentElement.style.setProperty(KB_INSET_PROPERTY, `${inset}px`);
  document.documentElement.style.setProperty(VV_HEIGHT_PROPERTY, `${scaledHeight}px`);

  let changed = false;
  const next = editableFocused && shrink > KEYBOARD_SHRINK_THRESHOLD_PX;
  if (next !== open) {
    open = next;
    changed = true;
  }
  const nextShort = scaledHeight < SHORT_VIEWPORT_PX;
  if (nextShort !== short) {
    short = nextShort;
    changed = true;
  }
  if (changed) notify();
}

/**
 * Start tracking. Idempotent; called once from `src/main.tsx`. A no-op where
 * `window.visualViewport` does not exist (the signal stays false and no
 * `--kb-inset` is written).
 */
export function startKeyboardTracking(): void {
  if (started) return;
  if (typeof window === 'undefined' || !window.visualViewport) return;
  started = true;

  const vv = window.visualViewport;
  const onViewport = () => {
    // A focused field removed from the DOM fires no focusout in every
    // browser, so re-check what actually holds focus before trusting it.
    if (editableFocused && !isEditable(document.activeElement)) editableFocused = false;
    update();
  };
  const onFocusIn = (event: FocusEvent) => {
    editableFocused = isEditable(event.target);
    update();
  };
  const onFocusOut = (event: FocusEvent) => {
    // `relatedTarget` is where focus is going (null when it leaves to body).
    editableFocused = isEditable(event.relatedTarget);
    update();
  };

  vv.addEventListener('resize', onViewport);
  vv.addEventListener('scroll', onViewport);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  teardown = () => {
    vv.removeEventListener('resize', onViewport);
    vv.removeEventListener('scroll', onViewport);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
  };

  editableFocused = isEditable(document.activeElement);
  update();
}

/** True while an on-screen keyboard is open (see the module comment). */
export function isKeyboardOpen(): boolean {
  return open;
}

export function subscribeKeyboard(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

export function getKeyboardSnapshot(): boolean {
  return open;
}

function getServerSnapshot(): boolean {
  return false;
}

/** The keyboard-open signal as React state. */
export function useKeyboardOpen(): boolean {
  return useSyncExternalStore(subscribeKeyboard, getKeyboardSnapshot, getServerSnapshot);
}

/** Story 24.17 — true while the visual viewport is under `SHORT_VIEWPORT_PX`. */
export function isShortViewport(): boolean {
  return short;
}

function getShortSnapshot(): boolean {
  return short;
}

/** The short-viewport signal as React state (false without `visualViewport`). */
export function useShortViewport(): boolean {
  return useSyncExternalStore(subscribeKeyboard, getShortSnapshot, getServerSnapshot);
}

/** Test seam: drop the listeners and every piece of state. */
export function __resetKeyboardTrackingForTests(): void {
  teardown?.();
  teardown = null;
  started = false;
  editableFocused = false;
  baseline = 0;
  lastInnerWidth = null;
  shrink = 0;
  if (typeof document !== 'undefined') {
    document.documentElement.style.removeProperty(KB_INSET_PROPERTY);
    document.documentElement.style.removeProperty(VV_HEIGHT_PROPERTY);
  }
  if (open || short) {
    open = false;
    short = false;
    notify();
  }
}
